import { expenseCategoryLabel } from "@gladis/ui/i18n";

/**
 * Finding a row in the recurring-expenses table.
 *
 * ==========================================================================================
 * A LOOKUP, NOT A FILTER — WHICH IS THE WHOLE DESIGN
 * ==========================================================================================
 *
 * It narrows the ROWS and moves no figure. The per-month total, the category strip and the four
 * stat cards go on describing the household, and the section says so in words.
 *
 * That is the opposite of what the `member` scope does, and the difference is not an inconsistency.
 * A scope names a SET somebody is responsible for, and its total is a real answer to a real question
 * — `scoped-share.ts` exists to compute it. A search names a substring. "The household is committed
 * to €75.00 per month" for whatever happened to contain the letters `net` is a figure nobody asked
 * for and cannot act on, and producing it would need the rows summed in the browser, which is a
 * second implementation of the product's rounding rule sitting beside the first.
 *
 * The archived toggle is the same class of control and has always behaved this way (FR-E8c, FR-E11).
 *
 * ==========================================================================================
 * IT MATCHES WHAT IS ON SCREEN
 * ==========================================================================================
 *
 * Description, the CATEGORY'S LABEL, and the card's name — the three pieces of text a reader can see
 * in a row. The category is matched through `expenseCategoryLabel` rather than on its stored value,
 * so a reader typing what the row says finds it: `housing` and `Housing` both work in English, and a
 * Dutch reader typing `wonen` finds the same row while `housing` no longer does. Searching for a word
 * that is not on screen in the language on screen is the surprising behaviour, not this.
 *
 * Interval and amount are deliberately absent. Both are already ordered by the column sort, and a
 * substring match on a formatted figure is a trap: `100` would match `€ 1,100.00` and `€ 100.00`
 * alike while `1.100` matches neither, depending on the language.
 */

/** Only what the matcher reads, so the row DTO can grow without touching this. */
export interface SearchableExpenseRow {
  description: string;
  category: string;
  card: { name: string } | null;
}

/**
 * A group row, as the matcher sees it.
 *
 * **A GROUP MATCHES ON ITS OWN LABEL OR ON ANY CHILD'S TEXT**, and the second half is what stops the
 * search lying. A member searching for "insurance" while the car's costs are folded into one row
 * would otherwise be told the household has no insurance — the row is on the page, and the word is
 * inside it.
 *
 * That also means a match found through a child should OPEN the fold, which `ExpenseList` does: a
 * row that matched for a reason the reader cannot see is worse than no match at all.
 */
export interface SearchableGroupRow {
  kind: "group";
  label: string;
  children: readonly SearchableExpenseRow[];
}

function isGroup(row: SearchableExpenseRow | SearchableGroupRow): row is SearchableGroupRow {
  return "kind" in row && row.kind === "group";
}

/**
 * The rows containing `query`, or every row when nothing has been typed.
 *
 * AN EMPTY QUERY IS NOT A MATCH OF NOTHING. A blank box means "no search", so it must return the
 * whole list rather than an empty one — and the same goes for a box holding only spaces, which is
 * what is left behind when somebody clears one character at a time.
 */
export function searchRows<T extends SearchableExpenseRow | SearchableGroupRow>(
  rows: readonly T[],
  query: string,
): T[] {
  const needle = fold(query);
  if (needle === "") return [...rows];

  return rows.filter((row) => fold(searchableTextOf(row)).includes(needle));
}

/** Whether this group would be found by `query` because of a CHILD rather than its own label. */
export function groupMatchedByChild(row: SearchableGroupRow, query: string): boolean {
  const needle = fold(query);
  if (needle === "") return false;
  if (fold(row.label).includes(needle)) return false;
  return row.children.some((child) => fold(searchableText(child)).includes(needle));
}

function searchableTextOf(row: SearchableExpenseRow | SearchableGroupRow): string {
  if (!isGroup(row)) return searchableText(row);
  return [row.label, ...row.children.map(searchableText)].join(" ");
}

/** Whether a search is narrowing the table — the same emptiness rule `searchRows` applies. */
export function isSearching(query: string): boolean {
  return fold(query) !== "";
}

function searchableText(row: SearchableExpenseRow): string {
  return [row.description, expenseCategoryLabel(row.category), row.card?.name ?? ""].join(" ");
}

/**
 * Case and accents removed, so typing what you remember finds what was entered.
 *
 * NFD SPLITS A LETTER FROM ITS ACCENT and the property escape then strips the accent, so `Éclair`
 * folds to `eclair` and is found by someone typing on a keyboard that cannot easily produce `É`. In a
 * Dutch household this is not an edge case: a street name with a diaeresis is ordinary, and a member
 * searching for one should not have to reproduce it.
 *
 * `toLowerCase` and NOT `toLocaleLowerCase`. Locale-sensitive lowercasing exists for cases like
 * Turkish dotted and dotless I, which neither of this application's two languages has — and using it
 * would mean the same query matching different rows depending on the language the interface happens
 * to be in, which is a surprise with nothing to buy it.
 */
function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}
